import { useCallback, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import type { Company, MemberRole } from "../lib/access";
import { Modal } from "../components/Modal";
import { PhoneInput, isE164 } from "../components/PhoneInput";

interface Member {
  id: string;
  user_id: string;
  role: MemberRole;
  status: string;
  phone_e164: string | null;
  profiles: { full_name: string | null } | null;
}
interface Invite {
  id: string; email: string; role: MemberRole; expires_at: string; accepted_at: string | null;
}

const ROLES: MemberRole[] = ["super_admin", "admin", "staff"];

export function People({ company, meId }: { company: Company; meId: string }) {
  const [members, setMembers] = useState<Member[] | null>(null);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MemberRole>("staff");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [handset, setHandset] = useState<Member | null>(null);
  const [phone, setPhone] = useState("");

  const canManage = company.role === "super_admin";

  const load = useCallback(async () => {
    const [m, i] = await Promise.all([
      supabase.from("memberships")
        .select("id,user_id,role,status,phone_e164,profiles(full_name)")
        .eq("company_id", company.id),
      supabase.from("invitations")
        .select("id,email,role,expires_at,accepted_at")
        .eq("company_id", company.id).is("accepted_at", null),
    ]);
    setMembers((m.data as unknown as Member[]) ?? []);
    setInvites((i.data as unknown as Invite[]) ?? []);
  }, [company.id]);

  useEffect(() => { void load(); }, [load]);

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setMsg(null);
    const { data, error } = await supabase.rpc("create_invitation", {
      p_company: company.id, p_email: email, p_role: role,
    });
    if (error) { setBusy(false); return setMsg(error.message); }
    const row = (Array.isArray(data) ? data[0] : data) as { invitation_id: string; token: string };

    const { data: sess } = await supabase.auth.getSession();
    const res = await fetch(`${import.meta.env.VITE_API_URL}/invitations/send`, {
      method: "POST",
      headers: { "Content-Type": "application/json",
                 Authorization: `Bearer ${sess.session?.access_token ?? ""}` },
      body: JSON.stringify({ invitation_id: row.invitation_id, token: row.token }),
    });
    setMsg(res.ok ? `Invitation emailed to ${email}.`
                  : "Invitation created, but the email did not send.");
    setEmail(""); setBusy(false); void load();
  }

  async function setRoleFor(id: string, next: MemberRole) {
    await supabase.from("memberships").update({ role: next }).eq("id", id);
    void load();
  }
  /**
   * The number that rings when a call comes in. Set through the RPC rather
   * than a direct update: `authenticated` holds a table-wide UPDATE grant on
   * memberships, so this column is not writable from the browser by design.
   */
  async function saveHandset() {
    if (!handset) return;
    setBusy(true); setMsg(null);
    const { error } = await supabase.rpc("set_handset", {
      p_company: company.id,
      p_user: handset.user_id,
      p_phone: phone || null,
    });
    setBusy(false);
    if (error) return setMsg(error.message);
    setHandset(null);
    void load();
  }

  async function setStatus(id: string, status: string) {
    await supabase.from("memberships").update({ status }).eq("id", id);
    void load();
  }

  return (
    <>
      <header className="page-head row">
        <div>
          <h1>People</h1>
          <p className="muted">
            {canManage ? "Invite colleagues and decide what each of them can do."
                       : "Only a super admin can change who has access."}
          </p>
        </div>
        {canManage && (
          <button className="btn" onClick={() => { setMsg(null); setInviting(true); }}>
            Invite someone
          </button>
        )}
      </header>

      {msg && !inviting && !handset && <p className="status">{msg}</p>}

      {inviting && (
        <Modal
          title="Invite someone"
          description="They get an email with a link that works once."
          onClose={() => setInviting(false)}
        >
          <form onSubmit={invite}>
            <label htmlFor="ie">Email</label>
            <input id="ie" type="email" required value={email} autoComplete="email"
              onChange={(e) => setEmail(e.target.value)} />

            <label htmlFor="ir">Role</label>
            <select id="ir" value={role} onChange={(e) => setRole(e.target.value as MemberRole)}>
              {ROLES.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
            </select>

            {msg && <p className="status">{msg}</p>}

            <div className="actions">
              <button className="btn" type="submit" disabled={busy || !email}>
                {busy ? "Sending…" : "Send invitation"}
              </button>
              <button className="btn ghost" type="button" onClick={() => setInviting(false)}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {handset && (
        <Modal
          title={`Handset for ${handset.profiles?.full_name ?? "this person"}`}
          description="The phone that rings when a call comes in. Without one, they are never dialled."
          onClose={() => setHandset(null)}
        >
          <form onSubmit={(e) => { e.preventDefault(); void saveHandset(); }}>
            <label htmlFor="hs">Their number</label>
            <PhoneInput id="hs" value={phone} onChange={setPhone} />
            {msg && <p className="status error">{msg}</p>}
            <div className="actions">
              <button className="btn" type="submit"
                disabled={busy || (phone !== "" && !isE164(phone))}>
                {busy ? "Saving…" : "Save handset"}
              </button>
              <button className="btn ghost" type="button" onClick={() => setHandset(null)}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Name</th><th>Role</th><th>Handset</th><th>Status</th></tr>
          </thead>
          <tbody>
            {(members ?? []).map((m) => (
              <tr key={m.id}>
                <td>
                  <b>{m.profiles?.full_name ?? "—"}</b>
                  {m.user_id === meId && <span className="sub">you</span>}
                </td>
                <td>
                  {canManage && m.user_id !== meId ? (
                    <select value={m.role}
                      onChange={(e) => void setRoleFor(m.id, e.target.value as MemberRole)}>
                      {ROLES.map((r) => <option key={r} value={r}>{r.replace("_", " ")}</option>)}
                    </select>
                  ) : m.role.replace("_", " ")}
                </td>
                <td className="nowrap">
                  {m.phone_e164 ?? <span className="muted">Not set</span>}
                  {(canManage || m.user_id === meId) && (
                    <button className="linkish" type="button"
                      onClick={() => { setMsg(null); setPhone(m.phone_e164 ?? ""); setHandset(m); }}>
                      {m.phone_e164 ? "Change" : "Add"}
                    </button>
                  )}
                </td>
                <td>
                  {canManage && m.user_id !== meId ? (
                    <button className="btn ghost small"
                      onClick={() => void setStatus(m.id, m.status === "active" ? "suspended" : "active")}>
                      {m.status === "active" ? "Suspend" : "Restore"}
                    </button>
                  ) : m.status}
                </td>
              </tr>
            ))}
            {invites.map((i) => (
              <tr key={i.id} className="pending">
                <td><b>{i.email}</b><span className="sub">invited, not yet accepted</span></td>
                <td>{i.role.replace("_", " ")}</td>
                <td className="muted">pending</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
